import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useProjectData } from './context/ProjectDataContext';
import RisksTable from './components/RisksTable';
import IssuesTable from './components/IssuesTable';

const ClientRisks = () => {
    // Everything on this page now comes from the context: it fetches risks and
    // issues on mount, in parallel with the project, and owns both WebSocket
    // refetch effects. This page used to fetch each itself, which cost a second
    // round trip that could not even start until `lead` arrived.
    //
    // preventRefreshRef must be the CONTEXT's ref, not a local one. The tables
    // set it around an edit and the context's effects check it; two different
    // refs would mean mid-edit refresh suppression silently stops working.
    const {
        tasks, members, lead, loading,
        issues, fetchIssues, risks, fetchRisks, risksLoaded, preventRefreshRef
    } = useProjectData();

    // Collapsible sections state with localStorage persistence
    const [collapsedSections, setCollapsedSections] = useState(() => {
        const saved = localStorage.getItem('risksIssuesCollapsed');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    // Persist collapse state
    useEffect(() => {
        localStorage.setItem('risksIssuesCollapsed', JSON.stringify([...collapsedSections]));
    }, [collapsedSections]);

    const toggleSection = (section) => {
        setCollapsedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    // Risks and issues arrive with the context, so once that is done they are
    // here. The frame appears as soon as project data is ready and the register
    // fills in when its own request lands.
    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Loading Risks & Issues...</p>
            </div>
        );
    }

    const risksCollapsed = collapsedSections.has('risks');
    const issuesCollapsed = collapsedSections.has('issues');

    return (
        <div className="h-full flex flex-col gap-2 p-4">
            {/* Risks Section */}
            <div className={`bg-white rounded-lg shadow flex flex-col overflow-hidden ${!risksCollapsed ? 'flex-1 min-h-0' : ''}`}>
                {/* Collapsible Header */}
                <div
                    className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center"
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                            onClick={() => toggleSection('risks')}
                        >
                        {risksCollapsed ? (
                            <FaChevronRight className="text-gray-500" />
                        ) : (
                            <FaChevronDown className="text-gray-500" />
                        )}
                        </span>
                        <h2 className="text-lg font-semibold text-gray-700">Risk Register</h2>
                        {/* The page no longer waits for risks before rendering, so
                            without this a still-loading register reads as "(0)" -
                            i.e. "you have no risks", which is not the same thing. */}
                        <span className="text-sm text-gray-500">({risksLoaded ? risks.length : '…'})</span>
                    </div>
                </div>

                {/* Content */}
                {!risksCollapsed && (
                    <div className="flex-1 overflow-hidden">
                        <RisksTable
                            risks={risks}
                            members={members || []}
                            onRefresh={fetchRisks}
                            leadId={lead?.id}
                            currentUserEmail={lead?.email}
                            isAdmin={false}
                            preventRefreshRef={preventRefreshRef}
                            hideHeader={true}
                        />
                    </div>
                )}
            </div>

            {/* Issues Section */}
            <div className={`bg-white rounded-lg shadow flex flex-col overflow-hidden ${!issuesCollapsed ? 'flex-1 min-h-0' : ''}`}>
                {/* Collapsible Header */}
                <div
                    className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center"
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                            onClick={() => toggleSection('issues')}
                        >
                        {issuesCollapsed ? (
                            <FaChevronRight className="text-gray-500" />
                        ) : (
                            <FaChevronDown className="text-gray-500" />
                        )}
                        </span>
                        <h2 className="text-lg font-semibold text-gray-700">Issues</h2>
                        <span className="text-sm text-gray-500">({issues.length})</span>
                    </div>
                </div>

                {/* Content */}
                {!issuesCollapsed && (
                    <div className="flex-1 overflow-hidden">
                        <IssuesTable
                            issues={issues}
                            members={members || []}
                            tasks={tasks || []}
                            onRefresh={fetchIssues}
                            leadId={lead?.id}
                            currentUserEmail={lead?.email}
                            isAdmin={false}
                            preventRefreshRef={preventRefreshRef}
                            hideHeader={true}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientRisks;
